import html2canvas from 'html2canvas';
import i18n from 'i18next';
import pdfMake from 'pdfmake';
import { useTheme } from '@material-ui/core';
import { allFindingTypeFiltersEnum } from '../../finding/models/findingTypeFilterEnum';
import findingTypeEnum from '../../finding/models/findingTypeEnum';
import priorityEnum from '../../finding/models/priorityEnum';
import creationModeEnum from '../../finding/models/creationModeEnum';
import { formatDate } from '../../common/formatters/timeFormatters';
import { chunkArray } from '../../common/formatters/arrayFormatter';

let theme;

const getTitle = (patrol) => ({ text: `Report for Patrol ${patrol.id}`, margin: [0, 0, 0, 30], fontSize: 18, bold: true });
const warningImagePath = 'https://portalthreatimages.blob.core.windows.net/threatimagesdev/warningIcon.png';

const getMissionDetails = async () => {
  const missionDetailsDocument = document.getElementById('missionDetails');
  if (missionDetailsDocument) missionDetailsDocument.querySelector('#buttonResyncPilot').style.display = 'none';
  const missionDetailsCanvas = await html2canvas(document.getElementById('missionDetails'));
  if (missionDetailsDocument) missionDetailsDocument.querySelector('#buttonResyncPilot').style.display = 'inherit';
  return { image: missionDetailsCanvas.toDataURL(), width: 525, margin: [0, 0, 0, 20] };
};

const getMissionMap = async () => {
  const missionMap = document.getElementById('map');
  if (missionMap) missionMap.querySelector('#styleButton').style.visibility = 'hidden';

  const missionMapCanvas = await html2canvas(missionMap);
  const result = { image: missionMapCanvas.toDataURL(), width: 535, margin: [0, 0, 0, 20] };

  if (missionMap) missionMap.querySelector('#styleButton').style.visibility = 'visible';

  return result;
};

const getFindingCharts = async () => {
  const findingChartsCanvas = await html2canvas(document.getElementById('findingCharts'));
  return { image: findingChartsCanvas.toDataURL(), width: 525 };
};

const getFindingTitle = (findingsByType, typeKey) => ({
  layout: 'noBorders',
  pageBreak: 'before',
  margin: [0, 20, 0, 60],
  table: {
    headerRows: 0,
    widths: [530],
    heights: [6, 30, 25],
    body: [
      [{ text: '', fillColor: theme.palette.findingType[typeKey] }],
      [
        {
          text: i18n.t(`finding.type.${typeKey}`),
          alignment: 'center',
          fillColor: theme.palette.findingType[typeKey],
          bold: true,
          color: '#FFFFFF',
          fontSize: 18,
        },
      ],
      [
        {
          text: `${findingsByType[allFindingTypeFiltersEnum[typeKey]].length} findings`,
          alignment: 'center',
          fillColor: theme.palette.findingType[typeKey],
          color: '#FFFFFF',
        },
      ],
    ],
  },
});

const getBase64ImageFromURL = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const dataURL = canvas.toDataURL('image/png');
      const base64String = dataURL.split(',')[1];

      resolve(base64String);
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = url;
  });

const getFindingDetails = async (finding, image) => {
  let imageContent;
  if (finding.overriden) {
    imageContent = await getBase64ImageFromURL(warningImagePath);
  } else {
    imageContent = await getBase64ImageFromURL(image.imagePath);
  }
  return {
    stack: [
      { text: finding.label, margin: [0, 0, 0, 3] },
      { image: `data:image/png;base64,${imageContent}`, width: 165, height: 120 },
      {
        text: `${i18n.t('patrol.finding.detectionAt')} ${formatDate(finding.detectedAt, finding.timezone)}`,
        fontSize: 10,
        alignment: 'right',
        margin: [0, 3, 0, 0],
      },
    ],
    width: 165,
    margin: [0, 0, 0, 30],
  };
};

const getFindingsByType = (findings) => {
  const findingsByType = {};

  Object.values(allFindingTypeFiltersEnum).forEach((type) => {
    findingsByType[type] = [];
  });

  findings.forEach((finding) => {
    if (finding.type === findingTypeEnum.threat) {
      if (finding.priority === priorityEnum.immediate) findingsByType[allFindingTypeFiltersEnum.immediate].push(finding);
      if (finding.priority === priorityEnum.investigation) findingsByType[allFindingTypeFiltersEnum.investigation].push(finding);
    }

    if (finding.type === findingTypeEnum.note) {
      if (finding.creationMode === creationModeEnum.automatic) findingsByType[allFindingTypeFiltersEnum.noteAutomatic].push(finding);
      if (finding.creationMode === creationModeEnum.manual) findingsByType[allFindingTypeFiltersEnum.noteManual].push(finding);
    }

    if (finding.type === findingTypeEnum.dismissed) findingsByType[allFindingTypeFiltersEnum.dismissed].push(finding);
    if (finding.type === findingTypeEnum.unclassified) findingsByType[allFindingTypeFiltersEnum.unclassified].push(finding);
  });

  return findingsByType;
};

const getFindings = async (findings, images) => {
  const result = [];
  const findingsByType = getFindingsByType(findings);

  // eslint-disable-next-line no-restricted-syntax
  for (const typeKey of Object.keys(allFindingTypeFiltersEnum)) {
    const filteredFindings = findingsByType[allFindingTypeFiltersEnum[typeKey]];
    // eslint-disable-next-line no-continue
    if (filteredFindings.length === 0) continue;

    result.push(getFindingTitle(findingsByType, typeKey));

    const findingsDetails = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const finding of filteredFindings) {
      const image = (images || []).find((x) => x.findingId === finding.id);
      if (!image) return null;

      // eslint-disable-next-line no-await-in-loop
      const details = await getFindingDetails(finding, image);
      findingsDetails.push(details);
    }

    chunkArray(findingsDetails, 3).forEach((columns) => {
      result.push({ columnGap: 18, columns });
    });
  }

  return result;
};

const getPdfContent = async (patrol, findings, images) => {
  const content = [];

  content.push(getTitle(patrol));
  content.push(await getMissionDetails());
  content.push(await getMissionMap());
  content.push(await getFindingCharts());
  content.push(await getFindings(findings, images));

  return content;
};

const printPdf = async (patrol, findings, images) => {
  if (!patrol) return;

  const content = await getPdfContent(patrol, findings, images);
  pdfMake
    .createPdf({
      content,
      defaultStyle: { font: 'Roboto' },
      pageSize: 'Letter',
    })
    .download(`patrol_report_${patrol.id}_${Date.now()}.pdf`);
};

const usePatrolPdf = () => {
  theme = useTheme();
  return printPdf;
};

export default usePatrolPdf;
